import React from 'react';
import { arrayOf, shape } from 'prop-types';

import { getAllServiceGenPagesWithSlug, getServiceGenPage, getMetadata } from '@/lib/graphcms';

import { Hero, BlockWrapper, Breadcrumb } from '@/components';

const Page = ({
  page: { blocks = [], stateData, breadcrumb, cityData, neighborhoodData, hero = null, localBusinessSchema = null },
}) => (
  <>
    {breadcrumb && (
      <Breadcrumb {...breadcrumb} stateData={stateData} cityData={cityData} neighborhoodData={neighborhoodData} />
    )}
    {hero && <Hero {...hero} />}
    {localBusinessSchema && (
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(localBusinessSchema),
        }}
      />
    )}
    {breadcrumb?.breadcrumbSchema && (
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(breadcrumb.breadcrumbSchema),
        }}
      />
    )}
    {blocks?.map(({ id, ...rest }) => (
      <BlockWrapper key={id} {...rest} stateData={stateData} cityData={cityData} neighborhoodData={neighborhoodData} />
    ))}
  </>
);

Page.propTypes = {
  page: shape({
    blocks: arrayOf(shape({}).isRequired).isRequired,
  }).isRequired,
};

export default Page;

/** Below are NextJS build functions. Please test the build command if you touch these. */
export async function getStaticProps({ params: { serviceGenPage, state }, preview = false }) {
  let city;
  let neighborhood;
  let serviceName;

  if (serviceGenPage.includes('b')) {
    switch (serviceGenPage.length) {
      case 2:
        [serviceName] = serviceGenPage;
        serviceName = `${serviceName}/b`;
        break;
      case 3:
        [city, serviceName] = serviceGenPage;
        serviceName = `${serviceName}/b`;
        break;
      case 4:
        [city, neighborhood, serviceName] = serviceGenPage;
        serviceName = `${serviceName}/b`;
        break;
      default:
        throw new Error('Required');
    }
  } else {
    switch (serviceGenPage.length) {
      case 1:
        [serviceName] = serviceGenPage;
        break;
      case 2:
        [city, serviceName] = serviceGenPage;
        break;
      case 3:
        [city, neighborhood, serviceName] = serviceGenPage;
        break;
      default:
        throw new Error('Required');
    }
  }

  const promisePage = getServiceGenPage(state, city, neighborhood, serviceName, preview);
  const page = await promisePage;
  const metadata = await getMetadata(preview);

  return {
    props: {
      page: {
        ...page,
        localBusinessSchema: page.localBusinessSchema,
      },
      metadata,
    },
    revalidate: 60,
  };
}

export async function getStaticPaths() {
  const states = await getAllServiceGenPagesWithSlug();

  const slugs = [];
  states.forEach(state => {
    state.stateGenPages.forEach(stateGenPage => {
      const slug = [];
      if (stateGenPage.slug.includes('/b')) {
        slug.push(...stateGenPage.slug.split('/'));
      } else {
        slug.push(stateGenPage.slug);
      }
      slugs.push({ state: state.slug, serviceGenPage: [...slug] });
    });
    state.citiesV2.forEach(city => {
      city.cityGenPagesV2.forEach(cityGenPage => {
        const slug = [city.slug.replace(`${state.slug}/`, '')];
        if (cityGenPage.slug.includes('/b')) {
          slug.push(...cityGenPage.slug.split('/'));
        } else {
          slug.push(cityGenPage.slug);
        }
        slugs.push({
          state: state.slug,
          serviceGenPage: [...slug],
        });
      });
      city.neighborhoods.forEach(neighborhood => {
        neighborhood.neighborhoodGenPages.forEach(neighborhoodGenPage => {
          const slug = [city.slug.replace(`${state.slug}/`, ''), neighborhood.slug.replace(`${city.slug}/`, '')];
          if (neighborhoodGenPage.slug.includes('/b')) {
            slug.push(...neighborhoodGenPage.slug.split('/'));
          } else {
            slug.push(neighborhoodGenPage.slug);
          }
          slugs.push({
            state: state.slug,
            serviceGenPage: [
              city.slug.replace(`${state.slug}/`, ''),
              neighborhood.slug.replace(`${city.slug}/`, ''),
              neighborhoodGenPage.slug,
            ],
          });
        });
      });
    });
  });

  const paths = slugs.map(slug => ({
    params: slug,
  }));

  return {
    paths,
    fallback: false,
  };
}
